import { cn } from '@/lib/utils';
import type { NaceCode } from './searchSidebarOptions';

// Structural parent of a NACE code, derived from the row + level.
// Levels 1 = section letter (A–U), 2 = "NN" division, 3 = "NN.N",
// 4 = "NN.NN", 5 = "NN.NN.N". The section letter for a division comes
// from the row's level1 column — the DB's authoritative WZ 2025 mapping,
// which diverges from NACE Rev.2.
export const getNaceParent = (item: NaceCode): string | null => {
  if (item.level <= 1) return null;
  if (item.level === 2) return item.level1 ?? null;
  if (item.level === 3) return item.code.slice(0, 2);
  if (item.level === 4) return item.code.slice(0, 4);
  if (item.level === 5) return item.code.slice(0, 5);
  return null;
};

export const toggleSet = (
  val: string,
  setFn: React.Dispatch<React.SetStateAction<string[]>>,
) =>
  setFn((prev) =>
    prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val],
  );

export const removeSet = (
  val: string,
  setFn: React.Dispatch<React.SetStateAction<string[]>>,
) => setFn((prev) => prev.filter((x) => x !== val));

export const getAgeRangeLabel = (id: string) => {
  const labels: Record<string, string> = {
    'age-20-30': '20 – 30',
    'age-30-40': '30 – 40',
    'age-40-50': '40 – 50',
    'age-50-60': '50 – 60',
    'age-60-70': '60 – 70',
    'age-70+': '70+',
  };
  return labels[id] ?? id;
};

/** The NACE rows the picker shows: a flat search hit list, or the expanded tree. */
export function visibleNaceCodes(
  sortedNaceCodes: NaceCode[],
  naceSearch: string,
  expandedNaceNodes: Set<string>,
  naceByCode: Map<string, NaceCode>,
): NaceCode[] {
  const q = naceSearch.trim().toLowerCase().replace(/-/g, '');
  if (q.length >= 2) {
    return sortedNaceCodes.filter((c) => {
      const code = c.code.toLowerCase().replace(/-/g, '');
      const desc = c.description.toLowerCase().replace(/-/g, '');
      return code.includes(q) || desc.includes(q);
    });
  }
  return sortedNaceCodes.filter((c) => {
    if (c.level === 1) return true;
    // Walk every ancestor; node visible iff each is expanded.
    let cursor: NaceCode | undefined = c;
    while (cursor && cursor.level > 1) {
      const parentCode = getNaceParent(cursor);
      if (!parentCode) return false;
      if (!expandedNaceNodes.has(parentCode)) return false;
      cursor = naceByCode.get(parentCode);
    }
    return true;
  });
}

/** The sidebar <aside> classes: fullscreen overlay or inline dropdown on mobile, a collapsible panel on desktop. */
export function sidebarAsideClassName(mobileFullscreen: boolean, isOpen: boolean): string {
  return cn(
    'shrink-0 bg-background',
    // Mobile behavior depends on mobileFullscreen:
    //   true  → fullscreen overlay when open / hidden when closed
    //           (ImmoScout-style filter screen, used by /search)
    //   false → full-width inline dropdown with max-height animation
    //           (legacy behavior, used by radar)
    mobileFullscreen
      ? isOpen
        ? // Anchor below the mobile header via the `--mobile-header-height`
          // CSS variable that HeaderMobile publishes via ResizeObserver.
          // Header (z-50) stays visible above the overlay (z-40); the
          // bottom safe-area padding clears the iOS home indicator.
          'fixed inset-x-0 bottom-0 top-[var(--mobile-header-height,140px)] z-40 flex flex-col pb-[env(safe-area-inset-bottom)]'
        : 'hidden'
      : cn(
          'overflow-y-auto w-full transition-[max-height] duration-300 ease-in-out',
          isOpen ? 'max-h-[9999px] border-b' : 'max-h-0',
        ),
    // Desktop: in-flow left panel, animate width — same in both modes.
    'lg:relative lg:inset-auto lg:z-auto lg:overflow-hidden lg:overflow-y-visible lg:h-full lg:w-auto lg:transition-[width,min-width,max-width] lg:duration-300 lg:ease-in-out',
    isOpen
      ? 'lg:flex lg:max-h-none lg:w-[380px] lg:min-w-[380px] lg:max-w-[380px] lg:border-b-0 lg:border-r'
      : 'lg:flex lg:max-h-none lg:w-0 lg:min-w-0 lg:max-w-0',
  );
}
